import type { ChangeEvent, FocusEvent } from 'react'
import type React from 'react'
import { useCallback, useEffect } from 'react'

import { useUnit } from 'effector-react'

import { EmptyProvider } from '@/components/ui/empty/empty-provider.tsx'
import { Separator } from '@/components/ui/separator.tsx'
import { SettingsPageTitle } from '@/modules/creator-settings/SettingsPageTitle.tsx'
import { TimeInputSection } from '@/modules/expiring-fans/components/time-input-section.tsx'
import type { FollowUp } from '@/modules/message/components/follow-ups/types.ts'
import { resolver } from '@/modules/message/components/follow-ups/validation.ts'
import { MessageSequenceEditor } from '@/modules/message/components/sequence/message-sequence-editor'
import { MessageContextType } from '@/modules/message/components/sequence/types.ts'
import {
  $followUpMessages,
  $sendAfterMinutes,
  $sendAfterMinutesError,
  $sendIfNotSeenParentMessage,
  addMessage,
  removeMessage,
  setMessages,
  setSendAfterMinutes,
  setSendIfNotSeenParentMessage,
  updateMessage,
} from '@/modules/message/models.ts'
import { EmptyMessages } from '@/modules/online-mass-messages/ui/error/empty-messages'

const dialogDescription = (
  <>
    Use the button to insert the fan’s name, which will replace the label with
    their actual name. If the fan doesn’t have a name, the label will be
    replaced with the fallback name.
  </>
)

const editorComponents = [
  'text',
  'media',
  'price',
  'fallback',
  'customAndFanNameLabels',
] as const

const minTimeValue = 1
const maxMinutesValue = 60

interface Props {
  type: string
  enabled: boolean
  disabled?: boolean
  dialogTitle: string
  toggleChange: React.ChangeEventHandler<HTMLInputElement>
  data?: FollowUp | null
  customDescription?: string
}

export const FollowUpsMessages = ({
  type,
  enabled,
  disabled,
  dialogTitle,
  toggleChange,
  data,
  customDescription,
}: Props) => {
  const followUpMessages = useUnit($followUpMessages)
  const sendAfterMinutes = useUnit($sendAfterMinutes)
  const sendIfNotSeenParentMessage = useUnit($sendIfNotSeenParentMessage)
  const sendAfterMinutesError = useUnit($sendAfterMinutesError)

  useEffect(() => {
    if (data) {
      setMessages(data.sequence.messages || [])
      setSendAfterMinutes(data.sendAfterMinutes || 0)
      setSendIfNotSeenParentMessage(data.sendIfNotSeenParentMessage || false)
    }
  }, [data])

  const handleOnBlurTimeInput = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      if (!event.target.value) {
        setSendAfterMinutes(0)
      }
    },
    [],
  )

  const handleOnChangeIncludesFansCheckbox = useCallback(
    (event: ChangeEvent<HTMLInputElement>) =>
      setSendIfNotSeenParentMessage(event.target.checked),
    [],
  )

  return (
    <div className="flex flex-col">
      <SettingsPageTitle
        title="Follow-Up Message"
        toggleName="enable-follow-up-message"
        onChange={toggleChange}
        enabled={enabled}
        disabled={disabled}
        description={
          customDescription || (
            <>
              Automatically sends messages when a fan doesn&apos;t respond to
              the {type}
            </>
          )
        }
      />
      {enabled && (
        <>
          <Separator className="mt-8" />
          <TimeInputSection
            title="Set Time"
            description={`The amount of minutes when the fan will be messaged, after the ${type} has been sent out`}
            unit="Minutes"
            min={minTimeValue}
            max={maxMinutesValue}
            textClassName="max-w-[134px]"
            value={sendAfterMinutes}
            onBlur={handleOnBlurTimeInput}
            onChange={setSendAfterMinutes}
            disabled={disabled}
            errorText={sendAfterMinutesError}
          />

          <Separator />
          <div className="text-title flex items-center gap-2 py-8 font-medium">
            <input
              type="checkbox"
              onChange={handleOnChangeIncludesFansCheckbox}
              checked={sendIfNotSeenParentMessage}
              className="accent-primary-500"
              disabled={disabled}
            />
            Also include fans who have not seen the {type}
          </div>

          <Separator />
          <EmptyProvider render={() => <EmptyMessages action="Add Message" />}>
            <MessageSequenceEditor
              title="Follow-Up Messages"
              description="Every Message will be sent out once per fan"
              placeholder="Hey %name%, how are you?"
              addMessageLabel="Follow Up"
              dialogTitle={dialogTitle}
              dialogDescription={dialogDescription}
              components={editorComponents}
              schema={resolver}
              entries={followUpMessages}
              onCreate={addMessage}
              onUpdate={updateMessage}
              onRemove={removeMessage}
              type={MessageContextType.FollowUpMessage}
              disabled={disabled}
            />
          </EmptyProvider>
        </>
      )}
    </div>
  )
}
